/**
 * Where a visitor came from, kept until they sign up.
 *
 * The first landing that carries any campaign signal (utm_* tags, an ad
 * platform's click ID, or an outside referrer) is stored and never replaced,
 * so the student is credited to the ad that first brought them, even if they
 * sign up days later from a bookmark. It goes to the backend once, with the
 * signup, and is then cleared.
 *
 * Only labels and click IDs are kept; the referrer is reduced to its host, and
 * the landing page to its path, so no query string (which can carry tokens or
 * a search) is stored.
 */

const KEY = "prepai.attribution";
const PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "ttclid",
  "gclid",
  "ctwa_clid",
] as const;

export type Attribution = Partial<Record<(typeof PARAMS)[number], string>> & {
  referrer?: string;
  landing_path?: string;
  touched_at?: string;
};

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function storedAttribution(): Attribution | null {
  try {
    const raw = storage()?.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

export function clearAttribution(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    /* storage disabled */
  }
}

/** An outside referrer as `https://host/`, or nothing for our own pages or no referrer. */
function outsideReferrer(referrer: string, ownHost: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const url = new URL(referrer);
    if (url.hostname === ownHost) return undefined;
    return `${url.protocol}//${url.hostname}/`;
  } catch {
    return undefined;
  }
}

/**
 * Record this landing, if it is the first one with a signal. Call once on load.
 * Returns what is stored afterwards.
 */
export function captureAttribution(
  location: Pick<Location, "search" | "pathname" | "hostname"> = window.location,
  referrer: string = document.referrer,
  now: Date = new Date(),
): Attribution | null {
  const existing = storedAttribution();
  if (existing) return existing;

  const query = new URLSearchParams(location.search);
  const touch: Attribution = {};
  for (const name of PARAMS) {
    const value = query.get(name)?.trim();
    if (value) touch[name] = value.slice(0, 255);
  }
  const outside = outsideReferrer(referrer, location.hostname);
  if (outside) touch.referrer = outside;
  if (Object.keys(touch).length === 0) return null;

  touch.landing_path = location.pathname.slice(0, 255);
  touch.touched_at = now.toISOString();
  try {
    storage()?.setItem(KEY, JSON.stringify(touch));
  } catch {
    /* A first touch kept only in memory is lost on reload; nothing else breaks. */
  }
  return touch;
}
